/**
 * The rules of dragging something too heavy to lift: where the dragged thing should be,
 * and when the grip gives out. Pure — no three.js, no Rapier, no React.
 *
 * A drag is not a carry. A carried box hangs in front of the camera and swings with the
 * view; a dragged table stays on the ground where it was, and the player tows it. That is
 * the whole reason the offset below is in **world** space: turning on the spot must move
 * the table not at all, and an offset measured relative to the player's facing would swing
 * a table around them like a boom.
 */

import type { GroundPoint } from './profile/types';

/**
 * How far the table may fall behind where it should be before the player loses their
 * grip. A table jammed against a hedge stops; the player does not, and without this they
 * would tow an invisible tether the length of the maze and find the table waiting at the
 * far end the moment it came free.
 *
 * Half a metre is about a step: far enough that the ordinary shoving and scraping of a
 * drag along a corridor does not break it, short enough that a table which is properly
 * stuck lets go while the player can still see it.
 */
export const DRAG_LEASH = 0.5;

/**
 * Where the dragged thing belongs this frame: the player's position plus the offset
 * recorded when the drag started.
 */
export function draggedTarget(player: GroundPoint, offset: GroundPoint): GroundPoint {
  return { x: player.x + offset.x, z: player.z + offset.z };
}

/** Has the dragged thing fallen too far behind the place it was being pulled to? */
export function isLeashBroken(actual: GroundPoint, target: GroundPoint): boolean {
  return Math.hypot(actual.x - target.x, actual.z - target.z) > DRAG_LEASH;
}
